import React, { useEffect, useMemo, useState } from "react";
import {
  Clock,
  Users,
  ArrowLeft,
  PlayCircle,
  Download,
  Radio,
  ExternalLink,
  CalendarClock,
  CalendarX2,
} from "lucide-react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../../../firebase-config";
import { useAuth } from "../../../../../provider/Auth.provider";
import { findUserByUID } from "../../../../../service/findUsers/findUserByUID";
import { useLiveSessionUpdater } from "../../../../../service/modules/useLiveSessionUpdater";
import { notification } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import AttendanceBadge from "../../../../../components/attendance/AttendanceBadge";
import { useMyAttendanceByModule } from "../../../../../hooks/useMyAttendanceByModule";

// ------------------------------------------------------------
// Tailwind, mobile-first refactor of Mymodule
// - No CSS modules; everything uses Tailwind classes
// - Uses lucide-react icons for compact, readable UI
// - Accessible, responsive, and resilient fallbacks
// ------------------------------------------------------------

const goldGrad =
  "bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41] text-black";

function StatusPill({ status = "not_scheduled" }) {
  const map = {
    active: { Icon: Radio, cls: `${goldGrad} animate-pulse` },
    upcoming: { Icon: CalendarClock, cls: "bg-[#CF9F41] text-black" },
    ended: { Icon: CalendarX2, cls: "bg-gray-500 text-white" },
    not_scheduled: { Icon: CalendarClock, cls: "bg-[#404040] text-gray-300" },
    digital: { Icon: PlayCircle, cls: "bg-[#CF9F41] text-black" },
  };
  const { Icon, cls } = map[status] ?? map.not_scheduled;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${cls}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {status.replace("_", " ")}
    </span>
  );
}

function GradientButton({ icon: Icon, children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold shadow transition active:scale-[.99] ${goldGrad} hover:from-[#CF9F41] hover:to-[#B17E32] ${className}`}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  );
}

function GhostButton({ icon: Icon, children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 rounded-md border border-gray-700 px-3 py-2 text-sm text-gray-300 hover:bg-[#404040]/60 transition ${className}`}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  );
}

const imgFallback = (e) => {
  e.currentTarget.src = "https://placehold.co/400x200";
};

const Mymodule = () => {
  const { user } = useAuth();
  const { packageId } = useParams();
  const navigate = useNavigate();

  const [courseData, setCourseData] = useState(null);
  const [batchData, setBatchData] = useState({});
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [moduleThumbnails, setModuleThumbnails] = useState({});
  const [moduleData, setModuleData] = useState({}); // { moduleId: moduleDoc }

  // live session updater
  const { moduleSchedules } = useLiveSessionUpdater(user?.uid, 30000);
  const trainerIds = useMemo(() => {
    if (courseData?.packageType === "package_dp") return [];
    const ids = new Set();

    const batches = selectedBatchId
      ? [batchData[selectedBatchId]].filter(Boolean)
      : (courseData?.packageContainingBatches || [])
          .map((b) => (typeof b === "string" ? batchData[b] : b))
          .filter(Boolean);

    batches.forEach((b) => {
      if (!b?.moduleSettings) return;
      Object.values(b.moduleSettings).forEach((ms) => {
        if (ms?.moduleTrainerId) ids.add(ms.moduleTrainerId);
      });
    });

    return Array.from(ids);
  }, [
    courseData?.packageType,
    courseData?.packageContainingBatches,
    batchData,
    selectedBatchId,
  ]);

  // pick batchId from url
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const bid = urlParams.get("batchId");
    setSelectedBatchId(bid);
  }, []);

  // fetch course/package data
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!packageId) {
        notification.error({
          message: "Course ID not found",
          description: "Please try again",
          placement: "topRight",
          duration: 3,
        });
        navigate("/student/dashboard");
        return;
      }

      try {
        const lmsPackageCollection = collection(db, "lms_package");
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Database query timeout")), 10000)
        );

        let packageQuery = query(
          lmsPackageCollection,
          where("packageId", "==", packageId)
        );

        let packageSnapshot = await Promise.race([
          getDocs(packageQuery),
          timeoutPromise,
        ]);

        if (packageSnapshot.empty) {
          packageQuery = query(
            lmsPackageCollection,
            where("id", "==", packageId)
          );
          packageSnapshot = await Promise.race([
            getDocs(packageQuery),
            timeoutPromise,
          ]);
        }

        if (packageSnapshot.empty) {
          try {
            const docRef = doc(db, "lms_package", packageId);
            const docSnap = await Promise.race([
              getDoc(docRef),
              timeoutPromise,
            ]);
            if (docSnap.exists()) {
              setCourseData({ id: docSnap.id, ...docSnap.data() });
              return;
            }
          } catch (_) {
            // ignore
          }
        }

        if (!packageSnapshot.empty) {
          const courseDoc = packageSnapshot.docs[0];
          setCourseData({ id: courseDoc.id, ...courseDoc.data() });
        } else {
          setCourseData(null);
        }
      } catch (error) {
        console.error("Error fetching course data:", error);
        if (error.message === "Database query timeout") {
          notification.error({
            message: "Connection timeout",
            description: "Please check your internet connection and try again",
            placement: "topRight",
            duration: 5,
          });
        } else {
          notification.error({
            message: "Error loading course",
            description: "Please try again or contact admin",
            placement: "topRight",
            duration: 3,
          });
        }
        setCourseData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourseData();
  }, [packageId, navigate]);

  // fetch user data (optional)
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) return;
      try {
        await findUserByUID(user.uid);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, [user?.uid]);

  // fetch batches
  useEffect(() => {
    const fetchBatchData = async () => {
      if (!courseData?.packageContainingBatches) return;

      const batchIds = courseData.packageContainingBatches.filter(
        (b) => typeof b === "string"
      );

      const batchDataObj = {};
      for (const bid of batchIds) {
        try {
          const batchRef = doc(db, "lms_batch", bid);
          const snap = await getDoc(batchRef);
          if (snap.exists()) {
            batchDataObj[bid] = { id: snap.id, ...snap.data() };
          }
        } catch (err) {
          console.error(`Error fetching batch ${bid}:`, err);
        }
      }
      setBatchData(batchDataObj);
    };
    fetchBatchData();
  }, [courseData?.packageContainingBatches]);

  // fetch modules for both DP & LC
  useEffect(() => {
    const fetchModuleData = async () => {
      let moduleIds = [];

      if (
        courseData?.packageType === "package_dp" &&
        courseData?.packageContainingCourses
      ) {
        for (const courseId of courseData.packageContainingCourses) {
          try {
            const courseRef = doc(db, "lms_course", courseId);
            const courseSnap = await getDoc(courseRef);
            if (courseSnap.exists()) {
              const cdata = courseSnap.data();
              if (Array.isArray(cdata?.courseContainingModules)) {
                moduleIds.push(...cdata.courseContainingModules);
              }
            }
          } catch (err) {
            console.error(`Error fetching course ${courseId}:`, err);
          }
        }
      } else if (courseData?.packageContainingBatches) {
        courseData.packageContainingBatches.forEach((batch) => {
          const batchObj = typeof batch === "string" ? batchData[batch] : batch;
          if (batchObj?.moduleSettings) {
            Object.values(batchObj.moduleSettings).forEach((ms) => {
              if (ms?.moduleId) moduleIds.push(ms.moduleId);
            });
          }
        });
      } else {
        return;
      }

      moduleIds = [...new Set(moduleIds)];

      const thumbs = {};
      const dataMap = {};
      await Promise.all(
        moduleIds.map(async (mid) => {
          try {
            const moduleRef = doc(db, "lms_module", mid);
            const moduleSnap = await getDoc(moduleRef);
            if (moduleSnap.exists()) {
              const data = moduleSnap.data();
              thumbs[mid] = data.moduleBanner || null;
              dataMap[mid] = { ...data, _id: mid };
            }
          } catch (_) {}
        })
      );

      setModuleThumbnails(thumbs);
      setModuleData(dataMap);
    };

    fetchModuleData();
  }, [batchData, courseData]);

  // download helper
  const downloadModuleContent = (contentUrl, moduleName) => {
    if (!contentUrl) {
      notification.warning({
        message: "No content available",
        description: "This module doesn't have downloadable content",
        placement: "topRight",
      });
      return;
    }
    try {
      const a = document.createElement("a");
      a.href = contentUrl;
      a.download = `${moduleName}_content`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      notification.success({
        message: "Download started",
        description: `${moduleName} content download initiated`,
        placement: "topRight",
      });
    } catch (error) {
      console.error("Download error:", error);
      notification.error({
        message: "Download failed",
        description: "Unable to download the content. Please try again.",
        placement: "topRight",
      });
    }
  };

  const modules = useMemo(() => {
    if (!courseData) return [];

    const isDP = courseData?.packageType === "package_dp";

    // Helper to create a card object consistently
    const buildCard = ({
      batchId,
      moduleId,
      title,
      subtitle,
      status,
      image,
      liveStatus,
      joinLink,
      index,
    }) => ({
      id: `${batchId ?? "dp"}_${moduleId ?? "mod"}_${index}`,
      number: String(index + 1).padStart(2, "0"),
      title: title || "Module",
      subtitle: subtitle || (isDP ? "Digital Course" : "Batch"),
      image,
      liveStatus,
      joinLink,
      status,
      moduleId,
    });

    const result = [];

    if (isDP) {
      const entries = Object.entries(moduleData); // [[moduleId, data]]
      entries.forEach(([mid, data], i) => {
        result.push(
          buildCard({
            moduleId: mid,
            title: data?.moduleName ?? "Module",
            subtitle: `${courseData.packageName} • Digital`,
            status: "available",
            image:
              data?.moduleBanner ||
              courseData?.packageBanner ||
              "https://placehold.co/400x200",
            liveStatus: { isLive: false, status: "digital", canJoin: false },
            joinLink: null,
            index: i,
          })
        );
      });
      return result;
    }

    // Live course: aggregate from batches
    let idx = 0;
    const batches = courseData?.packageContainingBatches || [];

    if (selectedBatchId) {
      const b = batchData[selectedBatchId];
      if (b?.moduleSettings) {
        Object.values(b.moduleSettings).forEach((ms) => {
          const mid = ms?.moduleId;
          if (!mid) return;
          const mdata = moduleData[mid] || {};
          const sched = moduleSchedules.find((s) => s.moduleId === mid);
          const banner =
            moduleThumbnails[mid] ||
            b.batchBanner ||
            b.batchImage ||
            b.image ||
            b.banner ||
            "https://placehold.co/400x200";

          result.push(
            buildCard({
              batchId: selectedBatchId,
              moduleId: mid,
              title: ms?.moduleName || mdata?.moduleName || "Module",
              subtitle: b?.batchName || "Selected Batch",
              status: ms?.moduleInstallmentStatus ? "ongoing" : "upcoming",
              image: banner,
              liveStatus: sched?.liveStatus || {
                status: "not_scheduled",
                isLive: false,
              },
              joinLink: sched?.liveLink || null,
              index: idx++,
            })
          );
        });
      } else {
        // Fallback card when batch not ready
        result.push(
          buildCard({
            batchId: selectedBatchId,
            moduleId: "fallback",
            title: "Module",
            subtitle: "Selected Batch",
            status: "upcoming",
            image: "https://placehold.co/400x200",
            liveStatus: { status: "not_scheduled" },
            joinLink: null,
            index: idx++,
          })
        );
      }
      return result;
    }

    // No selected batch: compile across all
    batches.forEach((batch, bIndex) => {
      const b = typeof batch === "string" ? batchData[batch] : batch;
      if (b?.moduleSettings) {
        Object.values(b.moduleSettings).forEach((ms) => {
          const mid = ms?.moduleId;
          if (!mid) return;
          const mdata = moduleData[mid] || {};
          const sched = moduleSchedules.find((s) => s.moduleId === mid);
          const banner =
            moduleThumbnails[mid] ||
            b.batchBanner ||
            b.batchImage ||
            b.image ||
            b.banner ||
            "https://placehold.co/400x200";

          result.push(
            buildCard({
              batchId: b?.id || batch,
              moduleId: mid,
              title: ms?.moduleName || mdata?.moduleName || "Module",
              subtitle: b?.batchName || `Batch ${bIndex + 1}`,
              status: ms?.moduleInstallmentStatus ? "ongoing" : "upcoming",
              image: banner,
              liveStatus: sched?.liveStatus || {
                status: "not_scheduled",
                isLive: false,
              },
              joinLink: sched?.liveLink || null,
              index: idx++,
            })
          );
        });
      } else if (b) {
        result.push(
          buildCard({
            batchId: b?.id || bIndex,
            moduleId: "fallback",
            title: "Module",
            subtitle: b?.batchName || `Batch ${bIndex + 1}`,
            status: "upcoming",
            image:
              b?.batchBanner ||
              b?.batchImage ||
              b?.image ||
              b?.banner ||
              "https://placehold.co/400x200",
            liveStatus: { status: "not_scheduled" },
            joinLink: null,
            index: idx++,
          })
        );
      }
    });

    return result;
  }, [
    courseData,
    batchData,
    moduleData,
    moduleThumbnails,
    moduleSchedules,
    selectedBatchId,
  ]);

  const isLive = courseData?.packageType === "package_lc";
  const params = new URLSearchParams(window.location.search);
  const batchId = params.get("batchId");

  const { loading: attLoading, byModule: attendanceByModule } =
    useMyAttendanceByModule(user?.uid, trainerIds, batchId);
  // console.log("lkjsadbflakjsfbalskjfbaskjfbaskjfbaskjfbaskjfbakjfbaskjbasjf,baskfjbqkfjkjcbn", attendanceByModule);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1E1E1E] p-4 text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-center py-24">
          <div className="flex flex-col items-center gap-4">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-700 border-t-\[\#CF9F41\]" />
            <p className="text-sm text-gray-300">Loading modules…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div className="min-h-screen bg-[#1E1E1E] p-4 text-white">
        <div className="mx-auto max-w-3xl rounded-lg border border-gray-700 bg-[#404040] p-6">
          <h2 className="mb-2 text-lg font-semibold text-\[\#CF9F41\]">
            Course Not Found
          </h2>
          <p className="mb-4 text-sm text-gray-300">
            The requested course could not be found for packageId: {packageId}
          </p>
          <GhostButton
            icon={ArrowLeft}
            onClick={() => navigate("/student/dashboard")}
          >
            Back to My Packages
          </GhostButton>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1E1E1E] p-4 text-white">
      {/* Header */}
      <div className="mx-auto mb-6 max-w-6xl">
        <GhostButton
          icon={ArrowLeft}
          onClick={() => {
            if (courseData?.packageType === "package_dp") {
              navigate("/student/dashboard");
            } else {
              navigate(`/student/dashboard/mypackage/course/${packageId}`);
            }
          }}
        >
          {courseData?.packageType === "package_dp"
            ? "Back to My Packages"
            : "Back to Course Details"}
        </GhostButton>
      </div>

      {/* Course header */}
      <div className="mx-auto mb-10 max-w-6xl rounded-xl bg-[#1E1E1E] p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <div className="mx-auto w-full max-w-md overflow-hidden rounded-lg">
              <img
                src={
                  selectedBatchId && batchData[selectedBatchId]
                    ? batchData[selectedBatchId].batchBanner ||
                      batchData[selectedBatchId].batchImage ||
                      batchData[selectedBatchId].moduleBanner ||
                      batchData[selectedBatchId].moduleImage ||
                      batchData[selectedBatchId].image ||
                      batchData[selectedBatchId].banner ||
                      courseData.packageBanner
                    : courseData.packageBanner || "https://placehold.co/400x200"
                }
                alt={courseData.packageName}
                className="h-56 w-full object-cover"
                onError={imgFallback}
              />
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
              <div>
                <h1 className="text-2xl font-bold">{courseData.packageName}</h1>
                <p className="text-sm text-gray-400">
                  {courseData.packageDifficulty}{" "}
                  {isLive ? "Live Course" : "Digital Product"}
                </p>
              </div>
              {isLive && (
                <span
                  className={`h-fit rounded-full px-3 py-1 text-xs font-bold ${goldGrad}`}
                >
                  LIVE
                </span>
              )}
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold">Description</h3>
              <div
                className="prose prose-invert max-w-none text-sm text-gray-300"
                dangerouslySetInnerHTML={{
                  __html:
                    courseData.packageDescription ||
                    "Course description not available.",
                }}
              />
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="flex flex-1 items-center gap-3 rounded-lg bg-[#404040] p-3">
                <Users className="h-5 w-5 text-\[\#CF9F41\]" />
                <div className="leading-tight">
                  <div className="text-base font-bold text-\[\#CF9F41\]">
                    {courseData.totalStudents || "65"}+
                  </div>
                  <div className="text-xs text-gray-300">Total Students</div>
                </div>
              </div>
              <div className="flex flex-1 items-center gap-3 rounded-lg bg-[#404040] p-3">
                <Clock className="h-5 w-5 text-\[\#CF9F41\]" />
                <div className="leading-tight">
                  <div className="text-base font-bold text-\[\#CF9F41\]">
                    {courseData.packageDuration || "6 Weeks"}
                  </div>
                  <div className="text-xs text-gray-300">Course Duration</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modules */}
      <div className="mx-auto max-w-6xl">
        <h2 className="mb-4 text-xl font-semibold">Module</h2>
        <div className="flex flex-col gap-4">
          {modules.map((m, i) => (
            <div
              key={m.id}
              className="flex flex-col gap-4 rounded-xl border border-gray-700 bg-[#404040] p-4 transition hover:border-\[\#CF9F41\]\/70 md:flex-row md:items-center md:gap-6"
            >
              {/* index */}
              <div className="flex items-center justify-center rounded-lg bg-[#404040] px-3 py-2 text-lg font-extrabold md:min-w-[64px] md:self-stretch">
                {m.number}
              </div>

              {/* content */}
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate text-base font-semibold">
                    {m.title}
                  </h3>
                  <StatusPill
                    status={m?.liveStatus?.status || "not_scheduled"}
                  />
                  {isLive && (
                    <AttendanceBadge
                      status={
                        attendanceByModule[m.moduleId]?.lastStatus ||
                        "not_marked"
                      }
                      counts={attendanceByModule[m.moduleId]}
                    />
                  )}
                </div>
                <p className="text-sm text-gray-400">{m.subtitle}</p>

                <div className="mt-3 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Join Link tile */}
                  <div className="flex items-start gap-3">
                    <div className="rounded-md bg-[#404040] p-2">
                      <ExternalLink className="h-5 w-5 text-[#CF9F41]" />
                    </div>
                    <div className="leading-tight">
                      <div className="text-sm font-semibold text-white">
                        Join Link
                      </div>
                      {m.joinLink ? (
                        <a
                          href={m.joinLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all text-[#CF9F41] underline underline-offset-4 decoration-[#CF9F41]/80 hover:opacity-90"
                        >
                          {m.joinLink}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">
                          No live session
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Course material tile */}
                  <div className="flex items-start gap-3">
                    <div className="rounded-md bg-[#404040] p-2">
                      <Download className="h-5 w-5 text-[#CF9F41]" />
                    </div>
                    <div className="leading-tight">
                      <div className="text-sm font-semibold text-white">
                        Course material
                      </div>
                      {(() => {
                        const currentModule = moduleData[m.moduleId];
                        const sched = moduleSchedules.find(
                          (s) => s.moduleId === m.moduleId
                        );
                        const isDigital =
                          sched?.moduleType === "digital" ||
                          courseData?.packageType === "package_dp";
                        const hasContent = currentModule?.moduleContent;

                        if (isDigital) {
                          return (
                            <button
                              onClick={() =>
                                navigate(
                                  `/student/dashboard/video/${m.moduleId}`
                                )
                              }
                              className="text-left text-[#CF9F41] underline underline-offset-4 decoration-[#CF9F41]/80 hover:opacity-90"
                            >
                              Watch Video
                            </button>
                          );
                        }

                        if (hasContent) {
                          return (
                            <button
                              onClick={() =>
                                downloadModuleContent(
                                  currentModule.moduleContent,
                                  currentModule.moduleName || m.title
                                )
                              }
                              className="text-left text-[#CF9F41] underline underline-offset-4 decoration-[#CF9F41]/80 hover:opacity-90"
                            >
                              Download All Materials
                            </button>
                          );
                        }

                        return (
                          <span className="text-sm text-gray-400">
                            No materials
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* thumbnail */}
              <div className="relative h-36 w-full overflow-hidden rounded-lg md:h-28 md:w-64">
                <img
                  src={m.image}
                  alt={m.title}
                  onError={imgFallback}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Mymodule;
